import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Form, Input, FormFeedback, Label, Button, Alert, Spinner } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser } from "../../../helpers/firebaseAuth";
import { ROLE_PM, ROLE_TENANT, ROLE_HOMES } from "../../../context/RoleContext";
import RoleToggle from "../RoleToggle";

const getPostLoginDest = (returnUrl, role) => {
  if (returnUrl) return returnUrl;
  return ROLE_HOMES[role] || "/tenant/home";
};

const LoginForm = ({ loading, setLoading, error, setError, returnUrl, mode, navigate, loginWithGoogle, checkAuth, onSuccess, onSignUp }) => {
  const [role, setRole] = useState(
    mode === ROLE_PM || mode === ROLE_TENANT ? mode : localStorage.getItem("ezron.activeRole") || ROLE_TENANT
  );

  const form = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      setError("");
      setLoading(true);
      try {
        localStorage.setItem("ezron.activeRole", role);
        const result = await loginUser(values.email, values.password);
        await checkAuth();

        if (result.requiresEmailVerification) {
          const target = returnUrl || getPostLoginDest(null, role);
          navigate(`/verify-email?returnUrl=${encodeURIComponent(target)}`);
          return;
        }

        if (onSuccess) {
          onSuccess();
          return;
        }

        navigate(getPostLoginDest(returnUrl, role));
      } catch {
        setError("Invalid email or password.");
        setLoading(false);
      }
    },
  });

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      localStorage.setItem("ezron.activeRole", role);
      await loginWithGoogle();
      await checkAuth();
      if (onSuccess) {
        onSuccess();
        return;
      }
      navigate(getPostLoginDest(returnUrl, role));
    } catch (err) {
      const code = err?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/popup-blocked"
      ) {
        setLoading(false);
        return;
      }
      setError("Google sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <p className="text-muted small mt-2 mb-0">Signing in…</p>
      </div>
    );
  }

  return (
    <Form onSubmit={form.handleSubmit}>
      {error && (
        <Alert color="danger" className="mb-3 py-2 small">
          <i className="bx bx-error-circle me-2" />{error}
        </Alert>
      )}

      <RoleToggle role={role} onChange={setRole} />

      <div className="mb-3">
        <Label className="form-label small fw-semibold">Email</Label>
        <Input
          name="email"
          type="email"
          placeholder="you@example.com"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values.email}
          invalid={form.touched.email && !!form.errors.email}
        />
        {form.touched.email && form.errors.email && (
          <FormFeedback>{form.errors.email}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Label className="form-label small fw-semibold mb-0">Password</Label>
          <Link to="/forgot-password" className="small text-muted">
            Forgot password?
          </Link>
        </div>
        <Input
          name="password"
          type="password"
          placeholder="••••••••"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values.password}
          invalid={form.touched.password && !!form.errors.password}
        />
        {form.touched.password && form.errors.password && (
          <FormFeedback>{form.errors.password}</FormFeedback>
        )}
      </div>

      <Button color="primary" className="w-100 mt-1" type="submit">
        Sign In
      </Button>

      <div className="d-flex align-items-center gap-2 my-3">
        <hr className="flex-grow-1 my-0" />
        <span className="text-muted small">or</span>
        <hr className="flex-grow-1 my-0" />
      </div>

      <button
        type="button"
        className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2"
        onClick={handleGoogleLogin}
      >
        <i className="mdi mdi-google fs-5" /> Continue with Google
      </button>

      <p className="text-center text-muted small mt-4 mb-0">
        Don&#39;t have an account?{" "}
        {onSignUp ? (
          <button type="button" className="btn btn-link p-0 align-baseline text-primary fw-semibold" onClick={onSignUp}>
            Sign up
          </button>
        ) : (
          <Link to="/register" className="text-primary fw-semibold">
            Sign up
          </Link>
        )}
      </p>
    </Form>
  );
};

export default LoginForm;
